/**
 * Reject confirmation modal — collects bilingual reason (English + Thai).
 * Both required by the backend (FCM payload picks the language matching
 * the submitter's pref). Submit is disabled until both fields have at
 * least one non-whitespace character.
 */
import { useEffect, useRef, useState } from 'react';

export default function RejectModal({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: { en: string; th: string }) => void;
}) {
  const [reasonEn, setReasonEn] = useState('');
  const [reasonTh, setReasonTh] = useState('');
  const enInputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      // Reset on open. Avoids reusing a stale value if the curator
      // opened+cancelled+reopened the modal for a different submission.
      setReasonEn('');
      setReasonTh('');
      // Focus shortly after the dialog is painted; Tailwind's class
      // transitions are instant so a microtask is enough.
      queueMicrotask(() => enInputRef.current?.focus());
    }
  }, [open]);

  if (!open) return null;

  const valid = reasonEn.trim().length > 0 && reasonTh.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reject-modal-title"
    >
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h2 id="reject-modal-title" className="text-lg font-semibold text-slate-900">
          Reject submission
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Provide a reason in both English and Thai. The submitter receives a push notification in
          their preferred language.
        </p>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Reason (English)</span>
            <textarea
              ref={enInputRef}
              value={reasonEn}
              onChange={(e) => setReasonEn(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-soi-500 focus:ring-1 focus:ring-soi-500"
              placeholder="e.g. Photo doesn't match the stall name."
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-slate-700">Reason (Thai)</span>
            <textarea
              value={reasonTh}
              onChange={(e) => setReasonTh(e.target.value)}
              rows={2}
              lang="th"
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-soi-500 focus:ring-1 focus:ring-soi-500"
              placeholder="เช่น รูปไม่ตรงกับชื่อร้าน"
            />
          </label>
        </div>
        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ en: reasonEn.trim(), th: reasonTh.trim() })}
            disabled={busy || !valid}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? 'Rejecting…' : 'Reject submission'}
          </button>
        </div>
      </div>
    </div>
  );
}
